<div class="flex flex-col border-b-2 border-grey-lightest pb-7">
	<div class="flex justify-between items-center">
		<div class="flex items-center">
			<app-icon class="text-2xl mr-3" [icon]="ICON.NOTE"></app-icon>
			<h2 class="title">File details</h2>
		</div>
		<app-button
			btn="tertiary"
			[icon]="ICON.EDIT"
			(click)="editMatterDetails()"
			[testHook]="testHooks.EditMatterDetailsButton"
		></app-button>
	</div>
	<div class="mt-5 [&>*]:whitespace-nowrap [&>div]:gap-4">
		<div class="flex">
			<span class="font-bold w-28 items-center">File status:</span
			><app-pill
				class="ml-2"
				[checkmark]="status === 'closed'"
				size="small"
				[mode]="status + 'Status'"
				[testHook]="testHooks.PillFileStatus"
				>{{ status | capitalize }}</app-pill
			>
		</div>
		<div class="flex mt-3">
			<span class="font-bold w-28 items-center">File name:</span
			><app-pill
				mode="secondary"
				textClass="truncate"
				class="ml-2 max-w-[25ch]"
				[matTooltip]="name.length >= 25 ? name : null"
				[testHook]="testHooks.PillFileName"
				><span>{{ name }}</span></app-pill
			>
		</div>
		<div class="flex mt-3">
			<span class="font-bold w-28 items-center">File number:</span
			><app-pill
				mode="secondary"
				textClass="truncate"
				class="ml-2 max-w-[25ch]"
				[matTooltip]="number.length >= 25 ? number : null"
				[testHook]="testHooks.PillFileNumber"
				><span>{{ number }}</span></app-pill
			>
		</div>
		<div *ngIf="accountingNumber" class="flex mt-3 items-center w-28">
			<span class="font-bold w-28">Acct. number:</span
			><app-pill
				mode="secondary"
				textClass="truncate"
				class="ml-2 max-w-[25ch]"
				[matTooltip]="accountingNumber.length >= 25 ? accountingNumber : null"
				[testHook]="testHooks.PillAccountingNumber"
				><span>{{ accountingNumber }}</span></app-pill
			>
		</div>
		<div class="flex mt-3">
			<span class="font-bold w-28 items-center">Office:</span>
			<div>
				<app-pill
					mode="secondary"
					class="ml-2"
					[testHook]="testHooks.PillOffice"
					>{{ office?.officeNickName || office?.officeName }}
					<ng-container *ngIf="office?.officeNickName">
						<br />({{ office?.officeName }})
					</ng-container>
				</app-pill>
			</div>
		</div>
		<div class="flex mt-3" *ngIf="fileType === fileTypes.REFINANCE">
			<span class="font-bold w-28">Acting for:</span
			><app-pill
				mode="secondary"
				class="ml-2"
				[testHook]="testHooks.PillRefinance"
				>{{ actingForDescription[actingFor] }}</app-pill
			>
		</div>
	</div>
	<div class="flex mt-3">
		<div class="flex items-center">
			<span class="mr-2 font-bold">Lawyer</span>
			<app-business-user-select-dropdown
				[selectedUserId]="assignedLawyer?.id"
				(selected)="setLawyer($event)"
				[role]="roles.LAWYER"
				xposition="left"
				[disabled]="checkForLLCorSyncMortgages$ | async"
			></app-business-user-select-dropdown>

			<span class="ml-5 mr-2 font-bold">Clerk</span>
			<app-business-user-select-dropdown
				[selectedUserId]="assignedClerk?.id"
				(selected)="setClerk($event)"
				[role]="roles.CLERK"
				xposition="left"
			></app-business-user-select-dropdown>
		</div>
	</div>
	<div
		class="flex mt-2 gap-2 text-grey text-sm"
		*ngIf="checkForLLCorSyncMortgages$ | async"
	>
		<app-icon [icon]="ICON.INFO"></app-icon>
		<p>
			Lawyer cannot be changed as mortgage instructions have been assigned to
			this lawyer
		</p>
	</div>
</div>
<div class="mt-7 border-b-2 border-grey-lightest pb-7">
	<div class="flex justify-between items-center">
		<div class="flex items-center">
			<app-icon class="text-2xl mr-3" [icon]="ICON.CALENDAR"></app-icon>
			<h2 class="title">Key dates</h2>
		</div>
		<app-button
			btn="tertiary"
			[icon]="ICON.EDIT"
			(click)="editKeyDates()"
			intercomAttr="editKeyDatesButton"
			[testHook]="testHooks.EditKeyDatesButton"
		></app-button>
	</div>
	<div class="flex flex-col gap-3 mt-2">
		<div class="flex">
			<div class="w-32 font-bold">Opening:</div>
			<app-pill mode="secondary" [testHook]="testHooks.PillOpening">
				<ng-container *ngIf="createdAt">{{
					createdAt | date: 'longDate'
				}}</ng-container>
				<ng-container *ngIf="!createdAt">NA</ng-container>
			</app-pill>
		</div>
		<div *ngIf="showRequisitionDate" class="flex">
			<div class="w-32 font-bold">Requisition:</div>
			<app-pill
				mode="secondary"
				*ngIf="titleSearchCompletionDate"
				[testHook]="testHooks.PillRequisition"
				>{{ titleSearchCompletionDate | date: 'longDate' }}</app-pill
			>
			<app-pill
				mode="secondary"
				*ngIf="!titleSearchCompletionDate"
				[testHook]="testHooks.PillCompletionNA"
				>NA</app-pill
			>
		</div>
		<div class="flex">
			<div class="w-32 font-bold">Appointment:</div>
			<app-pill mode="secondary" [testHook]="testHooks.PillAppointment"
				><span *ngIf="appointment?.appointmentTime"
					>{{ appointment?.appointmentTime | date: 'mediumDate' }}
					{{ appointment?.appointmentTime | date: 'shortTime' }}</span
				><span *ngIf="!appointment?.appointmentTime">NA</span></app-pill
			>
		</div>
		<div class="flex">
			<div class="w-32 font-bold">Closing:</div>
			<app-pill
				mode="secondary"
				*ngIf="closingDate"
				[testHook]="testHooks.PillClosing"
				>{{ closingDate | date: 'longDate' }}</app-pill
			>
			<app-pill
				mode="secondary"
				*ngIf="!closingDate"
				[testHook]="testHooks.PillClosingNA"
				>NA</app-pill
			>
		</div>
		<div class="flex" *ngIf="shouldShowOccupancyDate">
			<div class="w-32 font-bold">Occupancy:</div>
			<app-pill
				mode="secondary"
				*ngIf="occupancyDate"
				[testHook]="testHooks.PillOccupancy"
				>{{ occupancyDate | date: 'longDate' }}</app-pill
			>
			<app-pill
				mode="secondary"
				*ngIf="!occupancyDate"
				[testHook]="testHooks.PillOccupancyNA"
				>NA</app-pill
			>
		</div>
	</div>
</div>
<div
	class="flex flex-col mt-5 border-b-2 border-grey-lightest pb-7"
	*ngIf="address"
>
	<div class="flex justify-between items-center">
		<div class="flex items-center">
			<app-icon class="text-2xl mr-3" [icon]="ICON.LOCATION"></app-icon>
			<h2 class="title">Property</h2>
		</div>
	</div>

	<div
		class="mt-5 rounded-xl border border-solid transition overflow-hidden hover:shadow-md cursor-pointer"
		(click)="goToGoogleMaps()"
		[testHook]="testHooks.GoToGoogleMapsDiv"
	>
		<img [src]="locationMapUrl" />
	</div>

	<div class="flex flex-wrap gap-2">
		<div class="min-w-property-col">
			<div
				class="text-lg font-bold cursor-pointer hover:underline mt-5"
				(click)="goToPropertyDetails()"
				[testHook]="testHooks.PropertyAddressLink"
			>
				<span *ngIf="address?.unit">{{ address.unit }} - </span>
				<span *ngIf="address?.street">{{ address.street }}</span>
			</div>
			<span *ngIf="address?.street2" class="text-sm">{{
				address.street2
			}}</span>
			<div class="text-sm">
				<span *ngIf="address?.city">{{ address.city }}, </span>
				<span *ngIf="address?.province">{{ address.province }}</span>
			</div>
			<span class="text-sm" *ngIf="address?.postcode"
				>{{ address.postcode }},
			</span>
			<span class="text-sm" *ngIf="address?.country">{{
				address.country
			}}</span>
		</div>

		<div class="min-w-property-col">
			<div class="text-base text-grey-darker font-bold mt-5">Jurisdiction</div>
			<div>
				<ng-container
					*ngIf="primaryJurisdiction?.name; else emptyJurisdiction"
					>{{ primaryJurisdiction?.name }}</ng-container
				>
				<ng-template #emptyJurisdiction
					><span class="text-grey-light text-sm"
						>No jurisdiction selected</span
					></ng-template
				>
			</div>
			<div class="text-base text-grey-darker font-bold mt-5">LRO</div>
			<div>
				<ng-container *ngIf="lro?.selected; else emptyLRO"
					>{{ lro?.selected?.name }} ({{ lro?.selected?.id }})</ng-container
				>
				<ng-template #emptyLRO
					><span class="text-grey-light text-sm"
						>No LRO selected</span
					></ng-template
				>
			</div>
		</div>
	</div>

	<div
		*ngIf="propertyType?.type"
		class="flex flex-wrap mt-5 text-xs"
		(click)="goToPropertyDetails()"
		[testHook]="testHooks.PropertyDetails"
	>
		<app-pill
			class="propertyTypeInfo mr-2"
			mode="secondary"
			*ngIf="propertyType?.usage"
			[testHook]="testHooks.PillPropertyTypeUsage"
			>{{ propertyType?.usage | capitalize }}</app-pill
		>
		<app-pill
			class="propertyTypeInfo"
			mode="secondary"
			*ngIf="propertyTypeLabel?.length > 0"
			[testHook]="testHooks.PillPropertyTypeLabel"
			>{{ propertyTypeLabel }}</app-pill
		>
		<app-pill
			class="propertyTypeInfo"
			mode="secondary"
			*ngIf="propertyType?.status"
			[testHook]="testHooks.PillStatus"
			>{{ propertyType?.status | capitalize }}</app-pill
		>
		<app-pill
			class="propertyTypeInfo"
			mode="secondary"
			*ngIf="propertyType?.tenanted"
			[testHook]="testHooks.PillTenanted"
			>Tenanted</app-pill
		>
		<app-pill
			class="propertyTypeInfo"
			mode="secondary"
			*ngIf="propertyType?.parcelOfTiedLand"
			[testHook]="testHooks.PillParcelOfTiedLand"
			>POTL</app-pill
		>
	</div>
</div>

<div
	*ngIf="fileType !== fileTypes.REFINANCE"
	class="mt-7 border-b-2 border-grey-lightest pb-7"
>
	<div class="flex justify-between">
		<div class="flex items-center">
			<app-icon class="text-2xl mr-3" [icon]="ICON.KEY"></app-icon>
			<h2 class="title">Keys</h2>
		</div>
		<app-button
			btn="tertiary"
			[icon]="ICON.EDIT"
			(click)="editKeys()"
			[testHook]="testHooks.EditKeysButton"
		></app-button>
	</div>

	<div *ngIf="keys" class="mt-5 py-3">
		<app-pill *ngIf="keys?.released" [testHook]="testHooks.PillReleased"
			><app-icon [icon]="ICON.CHECK"></app-icon
			><span class="ml-2">Purchaser has received the keys</span></app-pill
		>
		<div *ngIf="!keys?.released">
			<p>Keys in possession of {{ possessionLabels[keys.possession] }}</p>
			<p *ngIf="keys?.possession === keysPossessions.LOCKBOX">
				Lockbox Code: {{ keys?.lockboxCode }}
			</p>
			<p *ngIf="keys?.note && keys?.note?.length > 0">{{ keys.note }}</p>
		</div>
	</div>
</div>

<div class="mt-7">
	<div class="flex items-center mb-3">
		<app-icon class="text-2xl mr-3" [icon]="ICON.DOLLAR"></app-icon>
		<h2 class="title">Payments</h2>
	</div>

	<app-container
		[border]="false"
		*ngIf="encumbrances.length > 0"
		[@openClose]
		class="bg-grey-lightest cursor-pointer transition hover:bg-grey-lighter"
	>
		<div
			class="flex flex-1 items-center"
			*ngFor="let encumbrance of encumbrances; trackBy: trackByEncumbranceId"
			(click)="goToEncumbrance(encumbrance)"
			[testHook]="testHooks.GoToEncumbranceDiv"
		>
			<span class="flex w-10 justify-center items-center">
				<app-icon [icon]="ICON.MORTGAGE" class="text-2xl text-grey"></app-icon>
			</span>
			<div class="ml-3 flex-1">
				<div *ngIf="encumbrance?.action !== encumbranceActions.DISCHARGE">
					<span>{{ getEncumbranceName(encumbrance?.type) }}</span> -
					<span class="text-sm font-bold">{{
						encumbrance?.action | capitalize
					}}</span>
				</div>
				<div *ngIf="encumbrance?.action === encumbranceActions.DISCHARGE">
					<span>{{ getEncumbranceName(encumbrance?.type) }}</span> -
					<span class="text-sm font-bold">{{
						mortgagePriorities[encumbrance?.priority]
					}}</span>
				</div>
				<span
					class="text-sm"
					[ngClass]="{
						'text-red-dark': !encumbrance?.fulfilled,
						'text-green': encumbrance?.fulfilled,
					}"
					*ngIf="encumbrance?.action === encumbranceActions.DISCHARGE"
					>●
					{{ encumbrance?.fulfilled ? 'Discharged' : 'To be discharged' }}</span
				>
			</div>
			<span class="font-bold flex items-center">{{
				encumbrance?.paymentCalc?.principalAmount | centsToDollars
			}}</span>
		</div>
	</app-container>

	<app-container
		[border]="false"
		class="mt-3 cursor-pointer bg-grey-lightest hover:bg-grey-lighter"
		(click)="goToBillsAndDebts()"
		[testHook]="testHooks.BillsAndDebtsContainer"
	>
		<div class="flex items-center">
			<span class="flex w-10 justify-center items-center">
				<app-icon [icon]="ICON.DOLLAR" class="text-2xl text-green"></app-icon>
			</span>
			<span class="flex-1 ml-3">Bills or debts to be paid</span>
			<span class="font-bold">{{ debtTotal | centsToDollars }}</span>
		</div>
	</app-container>
</div>
